/**
 * State machine SSE para POST /api/chat (Pattern 3 RESEARCH).
 * idle → classifying → searching → thinking → middleware_block → complete | error | cancelled
 *
 * Pitfall #4 RESEARCH cerrado: progress events `*_end` son no-op silentes;
 * la transición visible la dispara el siguiente `*_start` o terminal.
 */
import type { SseProgressEvent, SseTerminalEvent } from '../types/sse'
import type { ProductCard } from '../types/product_card'

export type SseState =
  | { status: 'idle' }
  | { status: 'classifying' }
  | { status: 'searching'; flow: string }
  | { status: 'thinking' }
  | { status: 'middleware_block'; reason: string; message: string }
  | { status: 'complete'; card: ProductCard | null; trace_id?: string | null }
  | { status: 'error'; message: string; retry: boolean }
  | { status: 'cancelled' }

export type SseAction =
  | { type: 'send' }
  | { type: 'progress'; evt: SseProgressEvent }
  | { type: 'terminal'; evt: SseTerminalEvent }
  | { type: 'reset' }

export function sseReducer(state: SseState, action: SseAction): SseState {
  switch (action.type) {
    case 'send':
      return { status: 'classifying' }
    case 'progress':
      switch (action.evt.type) {
        case 'classify_start':
          return { status: 'classifying' }
        case 'handler_start':
          return { status: 'searching', flow: action.evt.flow }
        case 'cl3_start':
          return { status: 'thinking' }
        case 'middleware_block':
          return {
            status: 'middleware_block',
            reason: action.evt.reason,
            message: action.evt.message,
          }
        // *_end events son no-op silentes — la transición visible la dispara
        // el siguiente *_start o el terminal correspondiente.
        default:
          return state
      }
    case 'terminal':
      switch (action.evt.type) {
        case 'complete':
          return {
            status: 'complete',
            card: action.evt.card,
            trace_id: action.evt.trace_id,
          }
        case 'error':
          // Drimian Spanish convention: action-oriented "¿Querés reintentar?"
          return {
            status: 'error',
            message: 'No pude procesar la consulta. ¿Querés reintentar?',
            retry: true,
          }
        case 'timeout':
          return {
            status: 'error',
            message: 'La consulta tardó demasiado. ¿Reintentar?',
            retry: true,
          }
        case 'cancelled':
          return { status: 'cancelled' }
      }
    // ESLint: no fallthrough — switch terminal exhausts
    // eslint-disable-next-line no-fallthrough
    case 'reset':
      return { status: 'idle' }
  }
}
